import H from '@here/maps-api-for-javascript';
import { isEqual } from 'lodash';
import { colors } from 'global/variables';
import { createMarkerSvg } from '../../../../icons/marker';
import { MARKER_ANCHOR } from '../../NewJobMarkers/utils/markerConstants';

const DEFAULT_STOP_MARKER_COLOR = {
  delivery: {
    inner: colors.defaultStopMarkerColor,
    outer: colors.defaultStopMarkerColor,
    text: colors.white,
  },
  pickup: {
    inner: colors.defaultStopMarkerColor,
    outer: colors.defaultStopMarkerColor,
    text: colors.white,
  },
};

export function getStopMarkerIcon(feature, highlight = false) {
  const jobMarkerColor = feature.properties?.jobMarkerColor || DEFAULT_STOP_MARKER_COLOR;

  const types = feature.properties?.types || ['delivery'];
  const typesObj = types.reduce((t, item) => {
    t[item] = (t[item] || 0) + 1;
    return t;
  }, {});

  const oneType = Object.keys(typesObj).length === 1;
  const baseColor = !oneType
    ? { inner: 'grey', outer: 'grey', text: 'white' }
    : Object.keys(typesObj)[0] === 'pickup'
    ? jobMarkerColor.pickup
    : jobMarkerColor.delivery;

  const isSquareIconType = oneType && (typesObj.recharge || typesObj.parking);
  const colorToUse =
    highlight && !isSquareIconType ? { ...baseColor, outer: 'yellow', text: 'yellow' } : baseColor;

  const jobOrder = feature.properties?.jobOrder || '';
  const highPriority = feature.properties?.highPriority || false;

  const iconTypes = [
    'break',
    'reload',
    'drivingRestTime',
    'workingRestTime',
    'pudo',
    'recharge',
    'parking',
    'dutyWorkingRestTime',
    'dutyDrivingRestTime',
  ];
  const hasIconType = oneType && Object.keys(typesObj).some((key) => iconTypes.includes(key));

  const baseSvg = createMarkerSvg(
    colorToUse.inner,
    colorToUse.outer,
    27,
    40,
    hasIconType ? '' : String(jobOrder),
    highPriority,
    typesObj,
    colorToUse.text,
    highlight,
    oneType,
    jobOrder,
  );

  return baseSvg;
}

export function setStopMarkerIcon(marker, feature, highlight = false) {
  const svg = getStopMarkerIcon(feature, highlight);
  marker.setIcon(new H.map.Icon(svg, { anchor: MARKER_ANCHOR.STOP }));
}

export function createStopMarker(coords, feature, highlight = false) {
  const marker = new H.map.Marker({ lat: coords[1], lng: coords[0] }, { volatility: true });
  marker.setData(feature.properties);
  setStopMarkerIcon(marker, feature, highlight);
  return marker;
}

export function updateStopGroupMarkers(
  geoJson,
  group,
  translationsRef,
  map,
  attachHoverEventsFn,
  highlightStop,
  selectedTour,
  dispatch,
  displayRef,
) {
  const features = geoJson?.geo?.features || [];
  const newMarkerIds = new Set();
  const existingMarkers = new Map();

  group.getObjects().forEach((marker) => {
    existingMarkers.set(marker.id, marker);
  });

  features.forEach((feature) => {
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates;
      const props = feature.properties || {};
      const id = props.jobId || `${coords[0]}_${coords[1]}_${props.routeId}`;
      newMarkerIds.add(id);

      const highlight =
        highlightStop !== undefined &&
        highlightStop !== null &&
        props.jobOrder === highlightStop &&
        props.routeId === selectedTour;

      const existingMarker = existingMarkers.get(id);

      if (existingMarker) {
        const currentData = existingMarker.getData() || {};
        const prevHighlight = existingMarker._isHighlighted || false;

        const needsIconUpdate =
          currentData.routeId !== props.routeId ||
          !isEqual(currentData.jobMarkerColor, props.jobMarkerColor) ||
          !isEqual(currentData.types, props.types) ||
          currentData.highPriority !== props.highPriority ||
          currentData.jobOrder !== props.jobOrder ||
          prevHighlight !== highlight;

        // Popup data (e.g. parkingId, labels, skills, ...) must always be refreshed even when
        // none of the icon-affecting fields above changed, otherwise the hover popup keeps
        // showing stale data from whenever this marker was first created/last updated.
        existingMarker.setData(props);
        existingMarker._isHighlighted = highlight;
        if (needsIconUpdate) {
          setStopMarkerIcon(existingMarker, feature, highlight);
        }
      } else {
        const marker = createStopMarker(coords, feature, highlight);
        marker.id = id;
        marker._isHighlighted = highlight;
        attachHoverEventsFn(marker, translationsRef, map, dispatch, displayRef);
        group.addObject(marker);
      }
    }
  });

  [...group.getObjects()].forEach((marker) => {
    if (!newMarkerIds.has(marker.id)) {
      group.removeObject(marker);
    }
  });

  group.setVisibility(true);
}
